import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Gauge } from 'lucide-react';

const pctColor = (pct) => (pct < 70 ? '#EF4444' : pct < 90 ? '#FB923C' : pct < 100 ? '#FACC15' : '#00C896');

const BLOCKING = [
  { key: 'needs_review', label: 'Not submitted by worker' },
  { key: 'submitted', label: 'Awaiting supervisor approval' },
  { key: 'returned', label: 'Returned to worker' },
  { key: 'payroll_exception', label: 'Flagged by payroll' },
  { key: 'sup_approved', label: 'Awaiting payroll approval' },
];

export const ReadinessWidget = ({ summary, onDrillDown }) => {
  const [showBlocking, setShowBlocking] = useState(false);
  if (!summary) return null;
  const pct = summary.readiness_pct ?? 0;
  const color = pctColor(pct);
  const c = summary.status_counts || {};
  const blockingItems = BLOCKING.map((b) => ({ ...b, count: c[b.key] || 0 })).filter((b) => b.count > 0);

  return (
    <div className="ht-card p-5" data-testid="payroll-ready-widget">
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${color}22` }}>
            <Gauge className="w-7 h-7" style={{ color }} />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: '#64748B' }}>Payroll Ready</p>
            <p className="text-5xl font-bold leading-none" style={{ color }} data-testid="payroll-ready-pct">{pct}%</p>
            <p className="text-[11px] mt-1" style={{ color: '#4A5568' }}>
              {summary.ready_entries}/{summary.total_entries} entries · {summary.period_start} → {summary.period_end}
            </p>
            {summary.attribution && (summary.attribution.no_project > 0 || summary.attribution.general_work > 0) && (
              <p className="text-[11px] mt-1" style={{ color: '#34D399' }} data-testid="readiness-attribution-line"
                title="Project attribution is for reporting and billing only — these hours are paid normally once approved or the period is locked.">
                {summary.attribution.no_project > 0 && `${summary.attribution.no_project} entr${summary.attribution.no_project === 1 ? 'y' : 'ies'} without a project (${summary.attribution.no_project_hours} h)`}
                {summary.attribution.no_project > 0 && summary.attribution.general_work > 0 && ' · '}
                {summary.attribution.general_work > 0 && `${summary.attribution.general_work} on General Work (${summary.attribution.general_work_hours} h)`}
                {' — payroll unaffected, attribution only'}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-3 ml-auto" data-testid="readiness-status-counts">
          {[
            ['Submitted', c.submitted || 0, '#60A5FA'],
            ['Supervisor Approved', c.sup_approved || 0, '#60A5FA'],
            ['Payroll Approved', (c.payroll_approved || 0) + (c.locked || 0), '#00C896'],
            ['Paid', c.paid || 0, '#00C896'],
          ].map(([label, n, col]) => (
            <div key={label} className="text-center px-3 py-2 rounded-xl" style={{ background: '#0B1321', border: '1px solid #2D3748' }}>
              <p className="text-xl font-bold" style={{ color: col }}>{n}</p>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: '#64748B' }}>{label}</p>
            </div>
          ))}
          <Button variant="outline" onClick={() => setShowBlocking(true)}
            disabled={pct >= 100}
            className="self-center border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            data-testid="view-blocking-btn">
            View What's Blocking
          </Button>
        </div>
      </div>

      {showBlocking && (
        <Dialog open onOpenChange={(o) => !o && setShowBlocking(false)}>
          <DialogContent className="max-w-md" data-testid="blocking-modal">
            <DialogHeader>
              <DialogTitle>What's blocking payroll?</DialogTitle>
              <DialogDescription>{summary.total_entries - summary.ready_entries} entr{summary.total_entries - summary.ready_entries === 1 ? 'y' : 'ies'} still need attention this period.</DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              {blockingItems.length === 0 ? (
                <p className="text-sm py-4 text-center" style={{ color: '#64748B' }}>Nothing is blocking — you're ready to lock. 🎉</p>
              ) : blockingItems.map((b) => (
                <div key={b.key} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5"
                  style={{ background: '#0B1321', border: '1px solid #2D3748' }}
                  data-testid={`blocking-category-${b.key}`}>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(255,160,0,0.15)', color: '#FBBF24' }}>{b.count}</span>
                    <span className="text-sm text-white">{b.label}</span>
                  </div>
                  <button onClick={() => { setShowBlocking(false); onDrillDown(b.key, b.label); }}
                    className="text-xs font-semibold px-2.5 py-1 rounded-lg"
                    style={{ background: 'rgba(30,136,229,0.15)', color: '#60A5FA' }}
                    data-testid={`blocking-details-${b.key}`}>
                    View Details
                  </button>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBlocking(false)} data-testid="blocking-modal-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ReadinessWidget;
